import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { fmtMoney } from '../../utils/money';
import { fmtDate } from '../../utils/dates';
import AlertsPanel from '../../components/financial/AlertsPanel';

export default function ProjectEdit() {
  const { funderId, month, projectId } = useParams();
  const { api } = useAuth();
  const toast = useToast();
  const [periodId, setPeriodId] = useState(null);
  const [data, setData] = useState(null);
  const [itemTypes, setItemTypes] = useState([]);
  const [lookups, setLookups] = useState(null);
  const [addingContract, setAddingContract] = useState(false);
  const [addingTransfer, setAddingTransfer] = useState(false);

  const reload = useCallback(async () => {
    const periods = await api(`/financial/funders/${funderId}/periods`);
    const cur = periods.find((p) => p.report_month === month) || periods[0];
    if (!cur) return;
    setPeriodId(cur.id);
    const detail = await api(`/financial/periods/${cur.id}/projects/${projectId}`);
    setData(detail);
  }, [funderId, month, projectId, api]);

  useEffect(() => {
    reload().catch((e) => toast.error(e.message));
    api(`/financial/project-item-types?project_id=${projectId}`).then(setItemTypes).catch(() => {});
    api('/financial/lookups').then(setLookups).catch(() => {});
  }, [reload, projectId, api, toast]);

  if (!data) return <div className="loader">جاري التحميل...</div>;
  const p = data.project;

  const del = async (url, label) => {
    if (!window.confirm(`تأكيد ${label}؟`)) return;
    try { await api(url, { method: 'DELETE' }); toast.success('تم الحذف'); reload(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div>
      <Link to={`/financial-reports/${funderId}/${month}/projects/${projectId}`} style={{ fontSize: 13, color: 'var(--text-muted)', display: 'inline-block', marginBottom: 8 }}>← عرض المشروع</Link>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 17 }}>تحرير: {p.name}</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
              المستهلَك {fmtMoney(p.total_used)} · المتبقّي {fmtMoney(p.total_remaining)} · التمويل {fmtMoney(p.funding_total)}
            </div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => setAddingContract((v) => !v)}>＋ إضافة عقد</button>
        </div>
      </div>

      {addingContract && (
        <ContractForm
          onCancel={() => setAddingContract(false)}
          onSubmit={async (vals) => {
            try {
              await api('/financial/contracts', { method: 'POST', body: JSON.stringify({ ...vals, project_id: projectId, report_period_id: periodId }) });
              toast.success('تم إنشاء العقد'); setAddingContract(false); reload();
            } catch (e) { toast.error(e.message); }
          }}
        />
      )}

      {itemTypes.length === 0 && (
        <div className="card" style={{ marginBottom: 16, borderInlineStart: '4px solid var(--warning)' }}>
          لم تُربط أنواع بنود بهذا المشروع بعد، لذا لن تتمكّن من إضافة بنود.{' '}
          <Link to="/financial-settings">اربط أنواع البنود من إعدادات التقارير المالية ←</Link>
        </div>
      )}

      {p.contracts.map((c) => (
        <ContractEditor
          key={c.id} contract={c} itemTypes={itemTypes} lookups={lookups} opsLinked={!!p.ops_project_id} api={api} toast={toast}
          onChange={reload} onDeleteContract={() => del(`/financial/contracts/${c.id}`, 'حذف العقد')}
        />
      ))}

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
          <div className="card-title">التحويلات</div>
          <button className="btn btn-outline btn-sm" onClick={() => setAddingTransfer((v) => !v)}>＋ تحويل</button>
        </div>
        {p.transfers.length > 0 && (
          <div className="table-wrap" style={{ marginBottom: addingTransfer ? 12 : 0 }}>
            <table>
              <thead><tr><th>من</th><th>إلى</th><th>المبلغ</th><th>المرجع</th><th></th></tr></thead>
              <tbody>
                {p.transfers.map((t) => (
                  <tr key={t.id}>
                    <td>{t.from_title || '—'}</td><td>{t.to_title || 'سداد/سحب'}</td>
                    <td>{fmtMoney(t.amount)}</td><td style={{ fontSize: 12 }}>{t.note || t.reference || '—'}</td>
                    <td><button className="btn btn-sm btn-danger" onClick={() => del(`/financial/balance-transfers/${t.id}`, 'حذف التحويل')}>حذف</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {addingTransfer && (
          <TransferForm
            contracts={p.contracts}
            onCancel={() => setAddingTransfer(false)}
            onSubmit={async (vals) => {
              try {
                await api('/financial/balance-transfers', { method: 'POST', body: JSON.stringify({ ...vals, report_period_id: periodId }) });
                toast.success('تم التحويل'); setAddingTransfer(false); reload();
              } catch (e) { toast.error(e.message); }
            }}
          />
        )}
      </div>

      <AlertsPanel alerts={data.alerts} />
    </div>
  );
}

function ContractEditor({ contract: c, itemTypes, lookups, opsLinked, api, toast, onChange, onDeleteContract }) {
  const [editing, setEditing] = useState(false);
  const [addItem, setAddItem] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const saveContract = async (vals) => {
    try { await api(`/financial/contracts/${c.id}`, { method: 'PUT', body: JSON.stringify(vals) }); toast.success('تم تحديث العقد'); setEditing(false); onChange(); }
    catch (e) { toast.error(e.message); }
  };
  const saveItem = async (vals, id) => {
    try {
      const url = id ? `/financial/report-items/${id}` : '/financial/report-items';
      await api(url, { method: id ? 'PUT' : 'POST', body: JSON.stringify({ ...vals, contract_id: c.id }) });
      toast.success('تم حفظ البند'); setAddItem(false); setEditItem(null); onChange();
    } catch (e) { toast.error(e.message); }
  };
  const delItem = async (id) => {
    if (!window.confirm('حذف البند؟')) return;
    try { await api(`/financial/report-items/${id}`, { method: 'DELETE' }); onChange(); } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontWeight: 700 }}>{c.title}{c.contract_no ? ` — ${c.contract_no}` : ''}
          <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 400 }}> · {c.start_date ? fmtDate(c.start_date) : '—'} ← {c.end_date ? fmtDate(c.end_date) : '—'}</span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-sm btn-outline" onClick={() => setEditing((v) => !v)}>✎ العقد</button>
          <button className="btn btn-sm btn-danger" onClick={onDeleteContract}>حذف</button>
        </div>
      </div>

      {editing && <ContractForm initial={c} onCancel={() => setEditing(false)} onSubmit={saveContract} />}

      <div className="table-wrap" style={{ marginTop: 10 }}>
        <table>
          <thead><tr><th>البند</th><th>المنفّذ</th><th>الكمية</th><th>السعر</th><th>السقف</th><th>المستهلَك</th><th></th></tr></thead>
          <tbody>
            {c.items.map((it) => (
              <tr key={it.id}>
                <td>{it.name_ar}{it.region !== 'none' ? ` (${it.region === 'inside_riyadh' ? 'داخل' : 'خارج'})` : ''}<SourceChip it={it} /></td>
                <td>{it.executed_label || (it.link ? linkSummary(it.link) : '—')}</td>
                <td>{it.effective_quantity ?? it.quantity ?? '—'}</td>
                <td>{it.unit_price != null ? fmtMoney(it.unit_price) : '—'}</td>
                <td>{it.ceiling_amount != null ? fmtMoney(it.ceiling_amount) : '—'}</td>
                <td>{fmtMoney(it.used_amount)}{it.used_amount_manual != null ? ' (يدوي)' : ''}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="btn btn-sm btn-outline" onClick={() => { setEditItem(it); setAddItem(false); }}>✎</button>{' '}
                  <button className="btn btn-sm btn-danger" onClick={() => delItem(it.id)}>حذف</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editItem && <ReportItemForm itemTypes={itemTypes} lookups={lookups} opsLinked={opsLinked} initial={editItem} onCancel={() => setEditItem(null)} onSubmit={(v) => saveItem(v, editItem.id)} />}

      {addItem
        ? <ReportItemForm itemTypes={itemTypes} lookups={lookups} opsLinked={opsLinked} onCancel={() => setAddItem(false)} onSubmit={(v) => saveItem(v)} />
        : <button className="btn btn-sm btn-outline" style={{ marginTop: 8 }} onClick={() => { setAddItem(true); setEditItem(null); }}>＋ إضافة بند</button>}
    </div>
  );
}

function ContractForm({ initial = {}, onSubmit, onCancel }) {
  const [v, setV] = useState({
    title: initial.title || '', sheikh_name: initial.sheikh_name || '', contract_no: initial.contract_no || '',
    start_date: initial.start_date || '', end_date: initial.end_date || '',
    funding_total: initial.funding_total ?? '', paid_amount: initial.paid_amount ?? '',
  });
  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));
  return (
    <div style={{ background: 'var(--surface-alt)', padding: 12, borderRadius: 6, marginTop: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
        <Field label="عنوان العقد"><input className="form-input" value={v.title} onChange={(e) => set('title', e.target.value)} /></Field>
        <Field label="الشيخ"><input className="form-input" value={v.sheikh_name} onChange={(e) => set('sheikh_name', e.target.value)} /></Field>
        <Field label="رقم العقد"><input className="form-input" value={v.contract_no} onChange={(e) => set('contract_no', e.target.value)} /></Field>
        <Field label="البداية"><input type="date" className="form-input" value={v.start_date || ''} onChange={(e) => set('start_date', e.target.value)} /></Field>
        <Field label="النهاية"><input type="date" className="form-input" value={v.end_date || ''} onChange={(e) => set('end_date', e.target.value)} /></Field>
        <Field label="إجمالي التمويل"><input type="number" className="form-input" value={v.funding_total} onChange={(e) => set('funding_total', e.target.value)} /></Field>
        <Field label="المدفوع"><input type="number" className="form-input" value={v.paid_amount} onChange={(e) => set('paid_amount', e.target.value)} /></Field>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button className="btn btn-primary btn-sm" onClick={() => onSubmit(clean(v))} disabled={!v.title}>حفظ</button>
        <button className="btn btn-outline btn-sm" onClick={onCancel}>إلغاء</button>
      </div>
    </div>
  );
}

function ReportItemForm({ itemTypes, lookups, opsLinked = true, initial = {}, onSubmit, onCancel }) {
  const measureForType = (itemType) => {
    const t = itemTypes.find((x) => x.item_type === itemType);
    return (t?.unit_price?.unit || '').includes('ساعة') ? 'hours' : 'count';
  };
  const [v, setV] = useState({
    item_type: initial.item_type || (itemTypes[0]?.item_type ?? ''),
    source: initial.source || 'manual',
    executed_label: initial.executed_label || '', quantity: initial.quantity ?? '',
    region: initial.region || 'none', unit_price: initial.unit_price ?? '',
    ceiling_amount: initial.ceiling_amount ?? '', used_amount_manual: initial.used_amount_manual ?? '',
    service_start_date: initial.service_start_date || '', note: initial.note || '',
    link_measure: initial.link_measure || 'count',
    link_task_status: initial.link_task_status || 'completed',
    link_workflow_id: initial.link_workflow_id || '',
    link_tag_id: initial.link_tag_id || '',
    link_sheikh_id: initial.link_sheikh_id || '',
  });
  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));
  const isLesson = v.item_type?.startsWith('lesson');
  const computed = (Number(v.quantity) || 0) * (Number(v.unit_price) || 0);

  const submit = () => {
    const body = {
      item_type: v.item_type, source: v.source, region: v.region,
      executed_label: v.executed_label || null,
      unit_price: v.unit_price === '' ? null : v.unit_price,
      ceiling_amount: v.ceiling_amount === '' ? null : v.ceiling_amount,
      used_amount_manual: v.used_amount_manual === '' ? null : v.used_amount_manual,
      service_start_date: v.service_start_date || null,
      note: v.note || null,
      quantity: v.source === 'manual' ? (v.quantity === '' ? null : v.quantity) : null,
      link_measure: v.source === 'tasks' ? v.link_measure : null,
      link_task_status: v.source === 'tasks' ? (v.link_task_status || null) : null,
      link_workflow_id: v.source === 'tasks' ? (v.link_workflow_id || null) : null,
      link_tag_id: v.source === 'tasks' ? (v.link_tag_id || null) : null,
      link_sheikh_id: v.source === 'tasks' ? (v.link_sheikh_id || null) : null,
    };
    onSubmit(body);
  };

  return (
    <div style={{ background: 'var(--surface-alt)', padding: 12, borderRadius: 6, marginTop: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
        <Field label="نوع البند">
          <select className="form-select" value={v.item_type} onChange={(e) => {
            const it = e.target.value;
            setV((s) => ({ ...s, item_type: it, link_measure: s.source === 'tasks' ? measureForType(it) : s.link_measure }));
          }}>
            {itemTypes.map((t) => <option key={t.item_type} value={t.item_type}>{t.unit_price?.name_ar || t.custom_name || t.item_type}</option>)}
          </select>
        </Field>
        <Field label="مصدر الكمية">
          <select className="form-select" value={v.source} onChange={(e) => {
            const src = e.target.value;
            setV((s) => ({ ...s, source: src, link_measure: src === 'tasks' ? measureForType(s.item_type) : s.link_measure }));
          }}>
            <option value="manual">يدوي (أُدخِل الكمية)</option>
            <option value="tasks">من المهام (المنصّة)</option>
            <option value="months">بالأشهر (من بدء الاحتساب)</option>
          </select>
        </Field>
        {isLesson && (
          <Field label="المنطقة">
            <select className="form-select" value={v.region} onChange={(e) => set('region', e.target.value)}>
              <option value="inside_riyadh">داخل الرياض (2000)</option>
              <option value="outside_riyadh">خارج الرياض (4000)</option>
            </select>
          </Field>
        )}
        <Field label="المنفّذ (وصف)"><input className="form-input" value={v.executed_label} onChange={(e) => set('executed_label', e.target.value)} /></Field>
        {v.source === 'manual' && (
          <Field label="الكمية"><input type="number" className="form-input" value={v.quantity} onChange={(e) => set('quantity', e.target.value)} /></Field>
        )}
        <Field label="سعر الوحدة (تلقائي إن تُرك)"><input type="number" className="form-input" placeholder="تلقائي" value={v.unit_price} onChange={(e) => set('unit_price', e.target.value)} /></Field>
        <Field label="السقف"><input type="number" className="form-input" value={v.ceiling_amount} onChange={(e) => set('ceiling_amount', e.target.value)} /></Field>
        <Field label="بدء الاحتساب"><input type="date" className="form-input" value={v.service_start_date || ''} onChange={(e) => set('service_start_date', e.target.value)} /></Field>
        <Field label="مبلغ يدوي (يَجُبّ المحسوب)"><input type="number" className="form-input" value={v.used_amount_manual} onChange={(e) => set('used_amount_manual', e.target.value)} /></Field>
        <Field label="ملاحظة"><input className="form-input" value={v.note} onChange={(e) => set('note', e.target.value)} /></Field>
      </div>

      {v.source === 'tasks' && (
        <div style={{ marginTop: 10, padding: 10, borderRadius: 6, border: '1px dashed var(--info)' }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
            تُحسب الكمية تلقائياً من مهام مشروع المنصّة المرتبط، تراكمياً حتى تاريخ القطع (تُجمَّد عند النشر).
          </div>
          {!opsLinked && (
            <div style={{ fontSize: 12, color: 'var(--warning)', marginBottom: 8 }}>
              ⚠ هذا المشروع غير مربوط بمشروع في المنصّة — ستبقى الكمية صفراً. اربطه من «تحرير المشروع» في لوحة الجهة.
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
            <Field label="القياس">
              <select className="form-select" value={v.link_measure} onChange={(e) => set('link_measure', e.target.value)}>
                <option value="count">عدد المهام</option>
                <option value="hours">ساعات (الوقت المسجَّل)</option>
              </select>
            </Field>
            <Field label="حالة المهمة">
              <select className="form-select" value={v.link_task_status} onChange={(e) => set('link_task_status', e.target.value)}>
                <option value="completed">مكتملة</option>
                <option value="in_progress">قيد التنفيذ</option>
                <option value="approved">معتمدة</option>
                <option value="pending">معلّقة</option>
              </select>
            </Field>
            <Field label="المسار (اختياري)">
              <select className="form-select" value={v.link_workflow_id} onChange={(e) => set('link_workflow_id', e.target.value)}>
                <option value="">الكل</option>
                {(lookups?.workflows || []).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </Field>
            <Field label="الوسم (اختياري)">
              <select className="form-select" value={v.link_tag_id} onChange={(e) => set('link_tag_id', e.target.value)}>
                <option value="">الكل</option>
                {(lookups?.tags || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </Field>
            <Field label="الشيخ (اختياري)">
              <select className="form-select" value={v.link_sheikh_id} onChange={(e) => set('link_sheikh_id', e.target.value)}>
                <option value="">الكل</option>
                {(lookups?.sheikhs || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
          </div>
        </div>
      )}
      {v.source === 'months' && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
          الكمية = عدد الأشهر من «بدء الاحتساب» حتى تاريخ القطع. تأكّد من تعبئة «بدء الاحتساب».
        </div>
      )}
      {v.source === 'manual' && computed > 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
          المحسوب = الكمية × السعر = {fmtMoney(computed)}
          {v.used_amount_manual !== '' && Number(v.used_amount_manual) !== computed && <span style={{ color: 'var(--warning)' }}> · سيُعتمد المبلغ اليدوي {fmtMoney(v.used_amount_manual)}</span>}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button className="btn btn-primary btn-sm" onClick={submit} disabled={!v.item_type}>حفظ</button>
        <button className="btn btn-outline btn-sm" onClick={onCancel}>إلغاء</button>
      </div>
    </div>
  );
}

function SourceChip({ it }) {
  if (!it.source || it.source === 'manual') return null;
  const label = it.source === 'tasks' ? (it.link?.frozen ? 'مهام (مجمّد)' : 'مهام') : 'أشهر';
  return <span style={{ fontSize: 10, marginInlineStart: 6, padding: '1px 6px', borderRadius: 8, background: 'var(--surface-alt)', color: 'var(--info)' }}>{label}</span>;
}

function linkSummary(link) {
  const parts = [link.measure === 'hours' ? 'ساعات' : 'عدد المهام'];
  if (link.workflow) parts.push(link.workflow);
  if (link.tag) parts.push(`#${link.tag}`);
  if (link.sheikh) parts.push(link.sheikh);
  return parts.join(' · ');
}

function TransferForm({ contracts, onSubmit, onCancel }) {
  const [v, setV] = useState({ from_contract_id: '', to_contract_id: '', amount: '', transfer_date: '', reference: '', note: '' });
  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));
  const src = contracts.find((c) => String(c.id) === String(v.from_contract_id));
  const insufficient = src && Number(v.amount) > src.total_remaining;

  return (
    <div style={{ background: 'var(--surface-alt)', padding: 12, borderRadius: 6 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
        <Field label="من عقد">
          <select className="form-select" value={v.from_contract_id} onChange={(e) => set('from_contract_id', e.target.value)}>
            <option value="">—</option>
            {contracts.map((c) => <option key={c.id} value={c.id}>{c.title} (متبقّي {fmtMoney(c.total_remaining)})</option>)}
          </select>
        </Field>
        <Field label="إلى عقد">
          <select className="form-select" value={v.to_contract_id} onChange={(e) => set('to_contract_id', e.target.value)}>
            <option value="">سداد / سحب</option>
            {contracts.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
        </Field>
        <Field label="المبلغ"><input type="number" className="form-input" value={v.amount} onChange={(e) => set('amount', e.target.value)} /></Field>
        <Field label="التاريخ"><input type="date" className="form-input" value={v.transfer_date || ''} onChange={(e) => set('transfer_date', e.target.value)} /></Field>
        <Field label="المرجع / التوجيه"><input className="form-input" value={v.reference} onChange={(e) => set('reference', e.target.value)} /></Field>
        <Field label="ملاحظة"><input className="form-input" value={v.note} onChange={(e) => set('note', e.target.value)} /></Field>
      </div>
      {insufficient && <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 6 }}>⚠ المبلغ يتجاوز رصيد العقد المصدر ({fmtMoney(src.total_remaining)}).</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button className="btn btn-primary btn-sm" onClick={() => onSubmit(clean(v))} disabled={!v.from_contract_id || !v.amount}>حفظ</button>
        <button className="btn btn-outline btn-sm" onClick={onCancel}>إلغاء</button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="form-label" style={{ fontSize: 12 }}>{label}</label>
      {children}
    </div>
  );
}

// يحذف الحقول الفارغة لتجنّب إرسال "" بدل null
function clean(obj) {
  const out = {};
  for (const [k, val] of Object.entries(obj)) {
    if (val !== '' && val !== null && val !== undefined) out[k] = val;
  }
  return out;
}
